import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, Send, Sparkles, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GREETING = "Bonjour ! Je suis votre conseillère VibeFit. Une question sur la plateforme vibrante, les coloris, la livraison ou les bienfaits ?";

export default function Assistant() {
  const [open, setOpen] = useState(false);
  const [model, setModel] = useState("claude");
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(
    localStorage.getItem("vf-session") ||
    (() => { const id = `sess-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`; localStorage.setItem("vf-session", id); return id; })()
  );
  const listRef = useRef(null);

  useEffect(() => { listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" }); }, [messages, open]);

  const appendLast = (chunk) => setMessages((m) => {
    const copy = [...m];
    copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
    return copy;
  });

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message: text, model }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          try {
            const payload = JSON.parse(part.slice(6));
            if (payload.delta) appendLast(payload.delta);
            if (payload.error) appendLast("Désolée, une erreur est survenue. Réessayez dans un instant.");
          } catch { /* ignore partial chunk */ }
        }
      }
    } catch {
      appendLast("Désolée, je suis momentanément indisponible. Réessayez dans un instant.");
    }
    setBusy(false);
  };

  return (
    <>
      <motion.button className="vf-assistant-bubble" onClick={() => setOpen(!open)} data-testid="assistant-open-button" aria-label="Ouvrir la conseillère VibeFit" whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.95 }}>
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </motion.button>
      <AnimatePresence>
        {open && (
          <motion.div className="vf-assistant" initial={{ opacity: 0, y: 30, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.97 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }} data-testid="assistant-panel">
            <div className="vf-assistant-head">
              <div>
                <span className="vf-assistant-kicker"><Sparkles size={12} /> Conseillère VibeFit</span>
                <div className="vf-assistant-models">
                  <button className={model === "claude" ? "active" : ""} onClick={() => setModel("claude")} data-testid="assistant-model-claude">Claude</button>
                  <button className={model === "gpt" ? "active" : ""} onClick={() => setModel("gpt")} data-testid="assistant-model-gpt">ChatGPT</button>
                  <button className={model === "gemini" ? "active" : ""} onClick={() => setModel("gemini")} data-testid="assistant-model-gemini">Gemini</button>
                </div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="assistant-close-button" aria-label="Fermer"><X size={18} /></button>
            </div>
            <div className="vf-assistant-messages" ref={listRef} data-testid="assistant-messages">
              {messages.map((m, i) => (
                <div key={i} className={`vf-msg ${m.role}`}>{m.content}{m.role === "assistant" && busy && i === messages.length - 1 && <span className="vf-caret">▍</span>}</div>
              ))}
            </div>
            <div className="vf-assistant-input">
              <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Écrivez votre question…" data-testid="assistant-input" />
              <button onClick={send} disabled={busy} data-testid="assistant-send-button" aria-label="Envoyer"><Send size={17} /></button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
